/**
 * Storage Compatibility Utilities - 新旧LocalStorage管理の互換性確保
 * Requirements: 8.1, 8.2, 8.3
 */

import { QuestionnaireProgress, ProgressAnswer } from '../../../types';
import { UserAnswer } from '../types';

// 既存のLocalStorageキー形式
const LEGACY_PROGRESS_KEY_PREFIX = 'questionnaire_progress_';
const LEGACY_ANSWERS_KEY_PREFIX = 'questionnaire_answers_';

// 新しいLocalStorageキー形式
const NEW_PROGRESS_KEY_PREFIX = 'gb_questionnaire_progress_';
const NEW_SESSION_KEY_PREFIX = 'gb_questionnaire_session_';

// レガシー進捗データの型定義
interface LegacyProgressData {
  questionSetId: string;
  currentQuestionIndex: number;
  answers: UserAnswer[];
  lastUpdatedAt: string;
  expiresAt: string;
}

// レガシー回答データの型定義
interface LegacyAnswerData {
  questionId: string;
  answerChoiceId: string;
  answeredAt: string;
}

/**
 * レガシー進捗データを新形式に変換
 */
export const migrateLegacyProgress = (questionSetId: string): QuestionnaireProgress | null => {
  try {
    const legacyProgressKey = `${LEGACY_PROGRESS_KEY_PREFIX}${questionSetId}`;
    const legacyAnswersKey = `${LEGACY_ANSWERS_KEY_PREFIX}${questionSetId}`;
    
    const legacyProgressData = localStorage.getItem(legacyProgressKey);
    const legacyAnswersData = localStorage.getItem(legacyAnswersKey);
    
    if (!legacyProgressData) {
      return null;
    }
    
    const legacyProgress: LegacyProgressData = JSON.parse(legacyProgressData);
    const legacyAnswers: LegacyAnswerData[] = legacyAnswersData 
      ? JSON.parse(legacyAnswersData) 
      : [];
    
    // 期限切れチェック
    const expiresAt = new Date(legacyProgress.expiresAt);
    if (expiresAt < new Date()) {
      // 期限切れの場合はクリーンアップ
      localStorage.removeItem(legacyProgressKey);
      localStorage.removeItem(legacyAnswersKey);
      return null;
    }
    
    // 新形式に変換
    const newProgress: QuestionnaireProgress = {
      sessionId: generateSessionId(),
      questionSetId: legacyProgress.questionSetId,
      currentQuestionIndex: legacyProgress.currentQuestionIndex,
      totalQuestions: legacyProgress.answers.length || 0,
      answers: convertLegacyAnswers(legacyAnswers),
      startedAt: new Date(legacyProgress.lastUpdatedAt), // 開始時刻は推定
      lastUpdatedAt: new Date(legacyProgress.lastUpdatedAt),
      expiresAt: new Date(legacyProgress.expiresAt),
      isCompleted: false,
      designVersion: 'legacy',
      metadata: {
        userAgent: navigator.userAgent,
        screenSize: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
        deviceType: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
      },
    };
    
    // 新形式で保存
    saveNewProgress(newProgress);
    
    // レガシーデータをクリーンアップ
    localStorage.removeItem(legacyProgressKey);
    localStorage.removeItem(legacyAnswersKey);
    
    return newProgress;
    
  } catch (error) {
    console.error('Legacy progress migration failed:', error);
    return null;
  }
};

/**
 * レガシー回答データを新形式（ProgressAnswer）に変換
 */
const convertLegacyAnswers = (legacyAnswers: LegacyAnswerData[]): Record<number, ProgressAnswer> => {
  const answers: Record<number, ProgressAnswer> = {};
  
  legacyAnswers.forEach((answer, index) => {
    answers[index] = { type: 'choice', answerChoiceId: answer.answerChoiceId };
  });
  
  return answers;
};

/**
 * 新形式の進捗データを保存
 */
export const saveNewProgress = (progress: QuestionnaireProgress): void => {
  try {
    const key = `${NEW_PROGRESS_KEY_PREFIX}${progress.questionSetId}`;
    localStorage.setItem(key, JSON.stringify(progress));
    
    // セッション管理用のキーも保存
    const sessionKey = `${NEW_SESSION_KEY_PREFIX}${progress.sessionId}`;
    localStorage.setItem(sessionKey, JSON.stringify({
      questionSetId: progress.questionSetId,
      createdAt: progress.startedAt,
      lastUpdatedAt: progress.lastUpdatedAt,
    }));
    
  } catch (error) {
    console.error('Failed to save new progress:', error);
  }
};

/**
 * 新形式の進捗データを読み込み
 */
export const loadNewProgress = (questionSetId: string): QuestionnaireProgress | null => {
  try {
    const key = `${NEW_PROGRESS_KEY_PREFIX}${questionSetId}`;
    const data = localStorage.getItem(key);
    
    if (!data) {
      return null;
    }
    
    const progress: QuestionnaireProgress = JSON.parse(data);
    
    // 期限切れチェック
    if (new Date(progress.expiresAt) < new Date()) {
      clearNewProgress(questionSetId);
      return null;
    }
    
    return progress;
    
  } catch (error) {
    console.error('Failed to load new progress:', error);
    return null;
  }
};

/**
 * 新形式の進捗データをクリア
 */
export const clearNewProgress = (questionSetId: string): void => {
  try {
    const progressKey = `${NEW_PROGRESS_KEY_PREFIX}${questionSetId}`;
    const progressData = localStorage.getItem(progressKey);
    
    if (progressData) {
      const progress: QuestionnaireProgress = JSON.parse(progressData);
      const sessionKey = `${NEW_SESSION_KEY_PREFIX}${progress.sessionId}`;
      localStorage.removeItem(sessionKey);
    }
    
    localStorage.removeItem(progressKey);
    
  } catch (error) {
    console.error('Failed to clear new progress:', error);
  }
};

/**
 * 統合された進捗データ読み込み（レガシー→新形式の自動移行付き）
 */
export const loadCompatibleProgress = (questionSetId: string): QuestionnaireProgress | null => {
  // まず新形式を試す
  let progress = loadNewProgress(questionSetId);
  
  if (!progress) {
    // 新形式がない場合はレガシーからの移行を試す
    progress = migrateLegacyProgress(questionSetId);
  }
  
  return progress;
};

/**
 * 全ての保存された進捗データを取得（新旧両方）
 */
export const getAllSavedProgress = (): QuestionnaireProgress[] => {
  const allProgress: QuestionnaireProgress[] = [];
  
  try {
    // LocalStorageの全キーをチェック
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // 新形式の進捗データ
      if (key.startsWith(NEW_PROGRESS_KEY_PREFIX)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const progress: QuestionnaireProgress = JSON.parse(data);
            // 期限切れでない場合のみ追加
            if (new Date(progress.expiresAt) >= new Date()) {
              allProgress.push(progress);
            }
          }
        } catch (error) {
          console.error(`Failed to parse progress data for key ${key}:`, error);
        }
      }
      
      // レガシー形式の進捗データ
      if (key.startsWith(LEGACY_PROGRESS_KEY_PREFIX)) {
        try {
          const questionSetId = key.replace(LEGACY_PROGRESS_KEY_PREFIX, '');
          const migratedProgress = migrateLegacyProgress(questionSetId);
          if (migratedProgress) {
            allProgress.push(migratedProgress);
          }
        } catch (error) {
          console.error(`Failed to migrate legacy progress for key ${key}:`, error);
        }
      }
    }
    
  } catch (error) {
    console.error('Failed to get all saved progress:', error);
  }
  
  return allProgress;
};

/**
 * 期限切れの進捗データをクリーンアップ
 */
export const cleanupExpiredProgress = (): { removedCount: number; errors: string[] } => {
  let removedCount = 0;
  const errors: string[] = [];
  
  try {
    const keysToRemove: string[] = [];
    
    // 期限切れのキーを特定
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      try {
        // 新形式の進捗データ
        if (key.startsWith(NEW_PROGRESS_KEY_PREFIX)) {
          const data = localStorage.getItem(key);
          if (data) {
            const progress: QuestionnaireProgress = JSON.parse(data);
            if (new Date(progress.expiresAt) < new Date()) {
              keysToRemove.push(key);
              // 対応するセッションキーも削除対象に追加
              const sessionKey = `${NEW_SESSION_KEY_PREFIX}${progress.sessionId}`;
              keysToRemove.push(sessionKey);
            }
          }
        }
        
        // レガシー形式の進捗データ
        if (key.startsWith(LEGACY_PROGRESS_KEY_PREFIX)) {
          const data = localStorage.getItem(key);
          if (data) {
            const legacyProgress: LegacyProgressData = JSON.parse(data);
            if (new Date(legacyProgress.expiresAt) < new Date()) {
              keysToRemove.push(key);
              // 対応する回答キーも削除対象に追加
              const questionSetId = key.replace(LEGACY_PROGRESS_KEY_PREFIX, '');
              const answersKey = `${LEGACY_ANSWERS_KEY_PREFIX}${questionSetId}`;
              keysToRemove.push(answersKey);
            }
          }
        }
        
      } catch (error) {
        errors.push(`Failed to check expiry for key ${key}: ${error}`);
      }
    }
    
    // 期限切れのキーを削除
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        removedCount++;
      } catch (error) {
        errors.push(`Failed to remove key ${key}: ${error}`);
      }
    });
    
  } catch (error) {
    errors.push(`Cleanup process failed: ${error}`);
  }
  
  return { removedCount, errors };
};

/**
 * LocalStorageの使用状況を取得
 */
export const getStorageUsage = (): {
  used: number;
  total: number;
  percentage: number;
  questionnaireDataSize: number;
} => {
  let used = 0;
  let questionnaireDataSize = 0;
  
  try {
    // 全データのサイズを計算
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        const itemSize = key.length + value.length;
        used += itemSize;
        
        // 質問回答関連データのサイズ
        if (key.startsWith(NEW_PROGRESS_KEY_PREFIX) || 
            key.startsWith(NEW_SESSION_KEY_PREFIX) ||
            key.startsWith(LEGACY_PROGRESS_KEY_PREFIX) ||
            key.startsWith(LEGACY_ANSWERS_KEY_PREFIX)) {
          questionnaireDataSize += itemSize;
        }
      }
    }
    
  } catch (error) {
    console.error('Failed to calculate storage usage:', error);
  }
  
  // LocalStorageの制限は通常5MB
  const total = 5 * 1024 * 1024; // 5MB in bytes
  const percentage = (used / total) * 100;
  
  return {
    used,
    total,
    percentage,
    questionnaireDataSize,
  };
};

/**
 * セッションIDを生成
 */
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 互換性チェック - レガシーデータの存在確認
 */
export const hasLegacyData = (): boolean => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LEGACY_PROGRESS_KEY_PREFIX)) {
        return true;
      }
    }
  } catch (error) {
    console.error('Failed to check for legacy data:', error);
  }
  
  return false;
};

/**
 * 開発用: 全ての質問回答関連データを表示
 */
export const debugStorageData = (): void => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  
  console.group('🔍 Questionnaire Storage Debug Info');
  
  const usage = getStorageUsage();
  console.log('📊 Storage Usage:', {
    used: `${Math.round(usage.used / 1024)}KB`,
    total: `${Math.round(usage.total / 1024 / 1024)}MB`,
    percentage: `${usage.percentage.toFixed(2)}%`,
    questionnaireData: `${Math.round(usage.questionnaireDataSize / 1024)}KB`,
  });
  
  const allProgress = getAllSavedProgress();
  console.log('📋 All Progress Data:', allProgress);
  
  console.log('🗂️ Has Legacy Data:', hasLegacyData());
  
  const cleanup = cleanupExpiredProgress();
  console.log('🧹 Cleanup Result:', cleanup);
  
  console.groupEnd();
};